import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import { getSpecialties, getAllCourses, getSpecialtyPath, getCoursePath } from '../utils/formationCatalog';
import FormationSelect from './FormationSelect';
import CountryCityFields from './CountryCityFields';
import { resolveDefaultCountry } from '../utils/locationData';
import { filterUpcomingSessions } from '../utils/sessionDates';
import { getMediaUrl } from '../utils/mediaUrl';
import {
  getFormationContentId,
  trackLeadIntent,
  prepareInscriptionSubmitTracking,
  trackCompleteRegistration,
} from '../utils/metaPixel';

const Formations = () => {
  const { getContent } = useContent();
  const location = useLocation();
  const pageHeader = getContent('formations.pageHeader') || {};
  const formationsGrid = getContent('formations.formationsGrid') || {};
  const calendar = getContent('formations.calendar') || {};
  const registrationForm = getContent('formations.registrationForm') || {};
  const contactForm = getContent('contact.contactForm') || {};
  const formationsCta = getContent('formations.cta') || {};
  const defaultCountry = resolveDefaultCountry(contactForm.defaultCountry);
  
  const specialties = useMemo(() => getSpecialties(getContent), [getContent]);
  const allCourses = useMemo(() => getAllCourses(getContent), [getContent]);

  const getFormationDetailPath = useCallback(
    (formation) => {
      const specialty = specialties.find((item) => item.id === formation.specialtyId);
      if (!specialty) return '/formations';
      return getCoursePath(specialty.slug || specialty.id, formation.slug || formation.id);
    },
    [specialties]
  );

  // Form submission states
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    country: defaultCountry,
    ville: '',
    formation: '',
    message: ''
  });
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [cityKey, setCityKey] = useState(0);
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formStatus.message) {
      setFormStatus({ type: '', message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'https://dentoxcellence-backend.dentoxcellence.workers.dev/api';

      const metaTracking = prepareInscriptionSubmitTracking({
        formationName: formData.formation,
        source: 'formations_page_form',
      });
      
      const response = await fetch(`${API_BASE}/inscription/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ville: [formData.ville, formData.country || selectedCountry].filter(Boolean).join(', '),
          ...metaTracking,
        }),
      });

      const data = await response.json();

      if (data.success) {
        trackCompleteRegistration({
          eventId: metaTracking.metaCompleteRegistrationEventId,
          formationName: formData.formation,
          source: 'formations_page_form',
        });
        setFormStatus({
          type: 'success',
          message: 'Votre demande d\'inscription a été envoyée avec succès ! Nous vous contacterons bientôt.'
        });
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          country: defaultCountry,
          ville: '',
          formation: '',
          message: ''
        });
        setSelectedCountry(defaultCountry);
        setCityKey((k) => k + 1);
      } else {
        setFormStatus({
          type: 'error',
          message: data.message || 'Erreur lors de l\'envoi. Veuillez réessayer.'
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({
        type: 'error',
        message: 'Erreur de connexion. Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Pre-fill "Formation d'intérêt" + scroll to registration form (same page) */
  const handleInscribeForFormation = useCallback((formation) => {
    if (!formation) return;
    trackLeadIntent({
      formationId: getFormationContentId(formation),
      formationName: formation.title,
      source: 'formations_calendar_inscribe',
    });
    setFormData(prev => ({ ...prev, formation: formation.title }));
    setFormStatus({ type: '', message: '' });
    window.requestAnimationFrame(() => {
      setTimeout(() => {
        document.getElementById('registration-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    });
  }, []);

  // URL ?formation=slug — pre-select formation (e.g. from FormationDetail or shared link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const slugParam = params.get('formation');
    if (!slugParam || !allCourses.length) return;
    const match = allCourses.find(f => f.slug === slugParam);
    if (match) {
      setFormData(prev => (prev.formation === match.title ? prev : { ...prev, formation: match.title }));
    }
  }, [location.search, allCourses]);

  useEffect(() => {
    const cards = document.querySelectorAll('.formation-card-detailed, .formation-card');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = 'running';
        }
      });
    });
    cards.forEach(card => observer.observe(card));

    return () => {
      cards.forEach(card => observer.unobserve(card));
    };
  }, [specialties.length]);

  // Scroll to hash / registration when URL has #registration-form or ?formation=
  useEffect(() => {
    const hash = location.hash?.replace('#', '') || '';
    const params = new URLSearchParams(location.search);
    const hasFormationParam = Boolean(params.get('formation'));

    const scrollTo = () => {
      if (hash === 'registration-form' || hasFormationParam) {
        document.getElementById('registration-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (hash) {
        const el = document.getElementById(hash) || document.querySelector(`.${hash}`);
        el?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const t = setTimeout(scrollTo, hasFormationParam || hash === 'registration-form' ? 180 : 100);
    return () => clearTimeout(t);
  }, [location.hash, location.search]);

  const renderPageHeaderButton = (button, index) => {
    const className = `btn ${button.type === 'primary' ? 'btn-primary' : 'btn-outline-white'}`;
    const link = button.link || '#';
    const content = (
      <>
        {button.icon && <i className={button.icon}></i>}
        {button.text}
      </>
    );

    if (/^https?:\/\//i.test(link) || link.startsWith('mailto:') || link.startsWith('tel:')) {
      return (
        <a
          key={index}
          href={link}
          className={className}
          target={button.target || '_blank'}
          rel="noopener noreferrer"
        >
          {content}
        </a>
      );
    }

    if (link.startsWith('#')) {
      return (
        <a key={index} href={link} className={className}>
          {content}
        </a>
      );
    }

    return (
      <Link key={index} to={link} className={className}>
        {content}
      </Link>
    );
  };

  const renderRegistrationFieldInput = (field) => (
    <div key={field.name} className="form-group">
      <label htmlFor={field.name}>
        {field.label} {field.required ? '*' : ''}
      </label>
      <input
        type={field.type}
        id={field.name}
        name={field.name}
        value={formData[field.name] || ''}
        onChange={handleInputChange}
        required={field.required}
        placeholder={field.placeholder || ''}
      />
    </div>
  );

  const renderRegistrationFields = () => {
    const fields = registrationForm.fields || [];
    const elements = [];
    let rowBuffer = [];

    const flushRow = () => {
      if (!rowBuffer.length) return;
      elements.push(
        <div key={`row-${elements.length}`} className="form-row">
          {rowBuffer}
        </div>
      );
      rowBuffer = [];
    };

    fields.forEach((field) => {
      if (field.name === 'formation' || field.type === 'select') {
        flushRow();
        elements.push(
          <div key={field.name} className="form-group">
            <label htmlFor="formation">
              {field.label || "Formation d'intérêt"} {field.required ? '*' : ''}
            </label>
            <FormationSelect
              getContent={getContent}
              id="formation"
              name="formation"
              value={formData.formation}
              onChange={handleInputChange}
              required={Boolean(field.required)}
            />
          </div>
        );
        return;
      }

      if (field.name === 'ville') {
        flushRow();
        elements.push(
          <CountryCityFields
            key="ville"
            contactForm={contactForm}
            renderCountry={false}
            cityName="ville"
            cityId="registration-ville"
            cityValue={formData.ville}
            onCityChange={(value) => setFormData((prev) => ({ ...prev, ville: value }))}
            activeCountry={formData.country || selectedCountry}
            cityResetKey={cityKey}
          />
        );
        return;
      }

      if (field.name === 'country') {
        return;
      }

      if (field.type === 'textarea') {
        flushRow();
        elements.push(
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>
              {field.label} {field.required ? '*' : ''}
            </label>
            <textarea
              id={field.name}
              name={field.name}
              rows="4"
              value={formData[field.name] || ''}
              onChange={handleInputChange}
              placeholder={field.placeholder || ''}
              required={field.required}
            />
          </div>
        );
        return;
      }

      if (field.name === 'telephone') {
        rowBuffer.push(renderRegistrationFieldInput(field));
        rowBuffer.push(
          <CountryCityFields
            key="country"
            contactForm={contactForm}
            renderCity={false}
            countryName="country"
            countryId="registration-country"
            countryValue={formData.country || selectedCountry}
            onCountryChange={(value) => {
              setSelectedCountry(value);
              setFormData((prev) => ({ ...prev, country: value, ville: '' }));
              setCityKey((k) => k + 1);
            }}
          />
        );
        flushRow();
        return;
      }

      rowBuffer.push(renderRegistrationFieldInput(field));
      if (rowBuffer.length === 2) flushRow();
    });

    flushRow();
    return elements;
  };

  return (
    <div className="formations-page">
      {/* Page Header */}
      <section className="page-header" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Hero Image Layer */}
        {pageHeader.heroImage && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0
          }}>
            <img
              src={getMediaUrl(pageHeader.heroImage)}
              alt="Hero"
              style={pageHeader.heroImageSettings ? {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(calc(-50% + ${pageHeader.heroImageSettings.position.x}px), calc(-50% + ${pageHeader.heroImageSettings.position.y}px)) scale(${pageHeader.heroImageSettings.scale})`,
                transformOrigin: 'center center',
                width: 'auto',
                height: 'auto',
                minWidth: '100%',
                minHeight: '100%',
                maxWidth: 'none',
                objectFit: 'cover'
              } : {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}
        
        {/* Gradient Overlay - Only if enabled */}
        {pageHeader.overlayEnabled !== false && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: pageHeader.overlayColor || '#58c4dc',
            opacity: pageHeader.overlayOpacity ?? 0.85,
            zIndex: 1
          }}></div>
        )}
        
        {/* Content */}
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="page-header-content">
            <h1>{pageHeader.title || 'Nos Formations'}</h1>
            <p>{pageHeader.subtitle || '6 spécialités dentaires pour maîtriser les techniques les plus avancées de la dentisterie moderne'}</p>
            {pageHeader.buttons && pageHeader.buttons.length > 0 && (
              <div className="page-header-buttons">
                {pageHeader.buttons.map(renderPageHeaderButton)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Specialty tiles hub */}
      <section className="formations-grid-section" id="formations-grid-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {specialties.length > 0
                ? `${specialties.length} Spécialité${specialties.length > 1 ? 's' : ''} d'Excellence`
                : formationsGrid.title || "Nos Formations d'Excellence"}
            </h2>
            <p className="section-subtitle">
              {formationsGrid.subtitle || 'Découvrez nos spécialités et programmes de formation'}
            </p>
          </div>

          <div className="formations-grid">
            {specialties.map((specialty) => (
              <Link
                key={specialty.id}
                to={getSpecialtyPath(specialty.slug || specialty.id)}
                className={`formation-card formation-specialty-tile formation-${specialty.color || 'blue'}`}
                id={specialty.id}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className="formation-specialty-tile__header">
                  <div className="formation-specialty-tile__icon">
                    <i className={specialty.icon || 'fas fa-graduation-cap'} />
                  </div>
                  <h2 className="formation-specialty-tile__title">{specialty.title}</h2>
                  {specialty.description && (
                    <p className="formation-specialty-tile__desc">
                      {specialty.description}
                    </p>
                  )}
                  <span className="formation-specialty-tile__cta">
                    <i className="fas fa-arrow-right" aria-hidden="true" />
                    Voir les formations
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Calendar Section (moved below formations) */}
      <section className="sessions-calendar" id="calendar">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{calendar.title || 'Calendrier Global des Sessions'}</h2>
            <p className="section-subtitle">
              {calendar.subtitle || 'Vue d\'ensemble de toutes nos prochaines formations'}
            </p>
          </div>

          <div className="calendar-grid">
            {allCourses.flatMap((formation) =>
              filterUpcomingSessions(formation.sessions || []).map((session, sessionIndex) => (
                <div key={`${formation.id}-${sessionIndex}`} className={`calendar-card formation-${formation.color}`}>
                  <div className="calendar-header">
                    <div className="calendar-icon">
                      <i className={formation.icon}></i>
                    </div>
                    <h4>{formation.title}</h4>
              </div>
              
                  <div className="calendar-details">
                    <div className="calendar-date">
                      <i className="fas fa-calendar"></i>
                      <strong>{session.date}</strong>
                    </div>
                    <div className="calendar-location">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{session.location}</span>
                    </div>
                    <div className="calendar-instructor">
                      <i className="fas fa-chalkboard-teacher"></i>
                      <span>{formation.instructor}</span>
                    </div>
                    <div className="calendar-spots">
                    <i className="fas fa-users"></i>
                      <span className="spots-available">{session.spots}</span>
                  </div>
                </div>
                
                  <div className="calendar-actions">
                    <Link
                      to={getFormationDetailPath(formation)}
                      className="btn btn-outline btn-full"
                    >
                      <i className="fas fa-info-circle"></i>
                      Voir la formation
                    </Link>
                    <a
                      href="#registration-form"
                      className="btn btn-primary btn-full"
                      onClick={(e) => {
                        e.preventDefault();
                        handleInscribeForFormation(formation);
                      }}
                    >
                      <i className="fas fa-user-plus"></i>
                      S'inscrire
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>
              </div>
      </section>

      {/* Registration Form */}
      <section className="registration-form-section" id="registration-form">
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>{registrationForm.title || 'Demande d\'Inscription'}</h2>
              <p>{registrationForm.subtitle || 'Remplissez ce formulaire pour recevoir plus d\'informations sur nos formations'}</p>
            </div>
            
            <form className="registration-form" onSubmit={handleSubmit}>
              {formStatus.message && (
                <div className={`form-alert ${formStatus.type}`}>
                  <i className={`fas ${formStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                  {formStatus.message}
                </div>
              )}

              {renderRegistrationFields()}
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary btn-large" disabled={isSubmitting}>
                  <i className={registrationForm.submitButton?.icon || "fas fa-paper-plane"}></i>
                  {isSubmitting ? 'Envoi en cours...' : (registrationForm.submitButton?.text || 'Envoyer la Demande')}
                </button>
                <p className="form-note">
                  <i className={registrationForm.securityNote?.icon || "fas fa-shield-alt"}></i>
                  {registrationForm.securityNote?.text || 'Vos informations sont sécurisées et ne seront pas partagées'}
                </p>
              </div>
            </form>

            <p className="form-note" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <Link to="/temoignages#submit" className="btn btn-outline btn-sm">
                <i className="fas fa-comment-dots"></i>
                Laisser un témoignage
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>{formationsCta.title || 'Prêt à Transformer Votre Pratique ?'}</h2>
            <p>{formationsCta.subtitle || 'Rejoignez plus de 500 dentistes qui ont fait confiance à DentoXcellence pour leur développement professionnel'}</p>
            <div className="cta-buttons">
              {(formationsCta.buttons || []).map((button, index) => (
                button.link?.startsWith('http') ? (
                  <a key={index}
                     href={button.link} 
                     className={`btn btn-${button.type}`}
                     target="_blank" 
                     rel="noopener noreferrer">
                    <i className={button.icon}></i>
                    {button.text}
                  </a>
                ) : (
                  <Link key={index} to={button.link} className={`btn btn-${button.type}`}>
                    <i className={button.icon}></i>
                    {button.text}
              </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Formations;